import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MapContainer, TileLayer, GeoJSON, useMapEvents, ImageOverlay, FeatureGroup } from 'react-leaflet';
import { EditControl } from "react-leaflet-draw";
import { point, booleanWithin, polygon } from "@turf/turf";
import LeafletLocateControl from '../../LeafletLocateControl';
import L from 'leaflet';

import { getParcelBounds } from '../../../../utils/map';
import { useParcelPlants } from '../../../../adapters/plants';
import { usePaletteScript, withPaletteScript } from '../../../../adapters/paletteRange';
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext';

// Center map on Granada
const position = [37.741934, -3.188515];

// Helper function to validate tree data structure
const hasValidTreeData = (data) => {
    return data && 
           typeof data === 'object' && 
           Array.isArray(data.features) && 
           data.features.length > 0;
};

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        height: '100%'
    },
}))

function MyComponent({ setNoteMarker, parcel }) {
    const map = useMapEvents({
        click: (e) => {
            var pts = point([e.latlng.lng, e.latlng.lat]);
            var searchWithin = polygon(parcel.geometry.coordinates);

            var ptsWithin = booleanWithin(pts, searchWithin);

            if (ptsWithin) setNoteMarker(e.latlng)
        },
    })
    return null
}

export default function NoteMap({ parcel, params, dispatch, marker, selectedResolution, layer, selectedLayerDate, showTrees }) {
    const classes = useStyles();
    const [map, setMap] = React.useState(null);

    // Fetch tree data for the parcel
    const { data: treeData, isLoading: isLoadingTrees, error: treeError } = useParcelPlants(parcel?.id);

    // Satellite image: the selected layer/date for a new note, or the ones saved with the note
    const imageLayerName = params ? params.layer_name : layer?.name;
    const imageDate = params ? params.layer_date : selectedLayerDate;

    // The user's saved palette adjustment for this farm/layer, so the note shows the same
    // colours as the main map (only in apps that enable palette adjustments). Saved notes
    // only store the layer name, so the layer is looked up by key or by name.
    const paletteEnabled = usePaletteAdjustmentsEnabled();
    const { data: palette, isLoading: paletteLoading } = usePaletteScript(
        { parcelId: parcel?.id, layerKey: params ? null : layer?.key, layerName: imageLayerName, date: imageDate },
        { enabled: paletteEnabled }
    );
    const imageUrl = imageLayerName && imageDate && parcel
        ? withPaletteScript(
            `${process.env.REACT_APP_BASE_URL_IMAGES}${selectedResolution.instance_id}?${parcel.properties.image_url}&LAYERS=${imageLayerName}&TIME=${imageDate}`,
            palette
        )
        : null;

    React.useEffect(() => {
        if (map && parcel) {
            map.fitBounds(getParcelBounds(parcel))
        }
    }, [map, parcel])

    // Debug logging for tree layer
    React.useEffect(() => {
        console.log('Tree layer debug:', {
            showTrees,
            isLoadingTrees,
            hasTreeData: hasValidTreeData(treeData),
            treeFeatureCount: treeData?.features?.length || 0,
            treeError: treeError?.message,
            parcelId: parcel?.id
        });
    }, [showTrees, treeData, isLoadingTrees, treeError, parcel?.id]);

    const onDeleted = (e) => {
        e.layers.eachLayer((layer) => {
            dispatch({ type: 'remove', marker: layer.toGeoJSON().geometry })
        });
    };

    const onEachFeature = (feature, layer) => {
        if (feature?.properties?.activity) layer.bindPopup(feature.properties.activity).openPopup();
    };

    return (
        <>
            {parcel && <MapContainer
                center={position}
                minZoom={2}
                maxZoom={24}
                zoom={9}
                className={classes.root}
                zoomControl={false}
                whenReady={(e) => {
                    // In React Leaflet v3, the map instance is in e.target
                    const mapInstance = e.target;
                    setMap(mapInstance);
                    
                    // Remove any existing locate controls
                    setTimeout(() => {
                        const locateControls = document.querySelectorAll('.leaflet-control-locate');
                        locateControls.forEach(control => {
                            if (!control.querySelector('.MuiPaper-root')) {
                                control.style.display = 'none';
                            }
                        });
                    }, 100);
                }}>

                {!marker && <FeatureGroup>
                    <EditControl
                        position='topright'
                        onCreated={(geom) => dispatch({ type: 'add', marker: geom.layer.toGeoJSON().geometry })}
                        onDeleted={(geom) => onDeleted(geom)}
                        draw={{
                            rectangle: false,
                            circle: false,
                            circlemarker: false,
                            polyline: false
                        }}
                    />
                </FeatureGroup>}

                <TileLayer
                        maxZoom={24}
                        maxNativeZoom={20}
                        attribution="Maps Hybrid"
                        url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`} />

                <GeoJSON data={parcel} color="#ffea00" name="Parcels" style={{ fillOpacity: 0 }} />

                {imageUrl && !paletteLoading && (
                    <ImageOverlay
                        key={imageUrl}
                        bounds={getParcelBounds(parcel)}
                        url={imageUrl}
                        transparent={true}
                        interactive>
                    </ImageOverlay>
                )}

                <LeafletLocateControl position="bottomRight" showDefaultControl={false} />

                {marker && <GeoJSON data={marker} color="blue" name="Geo Note" onEachFeature={onEachFeature} />}

                {showTrees && !isLoadingTrees && hasValidTreeData(treeData) && (
                    <GeoJSON 
                        key={`trees-${parcel?.id}-${treeData.features.length}`}
                        data={treeData} 
                        pointToLayer={(feature, latlng) => {
                            console.log('Creating tree marker at:', latlng, 'for feature:', feature.properties);
                            return L.circleMarker(latlng, {
                                radius: 5, // Increased for better visibility
                                fillColor: "#00FF00",
                                color: "#006400", // Darker green border for contrast
                                weight: 2, // Increased weight
                                opacity: 1,
                                fillOpacity: 1, // Full opacity for testing
                                pane: 'markerPane', // Explicit pane for proper layering
                                zIndexOffset: 1000 // Ensure it's on top
                            });
                        }}
                        style={{
                            zIndex: 1000
                        }}
                    />
                )}

                {showTrees && isLoadingTrees && (
                    <div style={{ 
                        position: 'absolute', 
                        top: 10, 
                        right: 10, 
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        padding: '5px',
                        borderRadius: '3px',
                        fontSize: '12px'
                    }}>
                        Loading trees...
                    </div>
                )}

                {showTrees && treeError && (
                    <div style={{ 
                        position: 'absolute', 
                        top: 10, 
                        right: 10, 
                        zIndex: 1000,
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        color: 'red',
                        padding: '5px',
                        borderRadius: '3px',
                        fontSize: '12px'
                    }}>
                        Error loading trees: {treeError.message}
                    </div>
                )}

            </MapContainer>}
        </>
    );
}
